//
// Copyright 2025 DXOS.org
//

// @import-as-namespace

import { type QueryAST } from '@dxos/echo-protocol';

export const OrderTypeId = '~@dxos/echo/Order' as const;
export type OrderTypeId = typeof OrderTypeId;

export interface Order<T> {
  readonly [OrderTypeId]: { value: T };

  ast: QueryAST.Order;
}

export type Any = Order<any>;

class OrderClass implements Order<any> {
  private static 'variance': Order<any>[OrderTypeId] = {} as Order<any>[OrderTypeId];

  static is(value: unknown): value is Any {
    return typeof value === 'object' && value !== null && OrderTypeId in value;
  }

  constructor(public readonly ast: QueryAST.Order) {}

  [OrderTypeId] = OrderClass.variance;
}

/**
 * Order by the database's default order. For non-feed sources this is by id; for feed sources
 * this is insertion order, so `desc` reads newest-first. Defaults to `asc`.
 *
 * @performance O(1); builds an order AST node without sorting anything.
 */
export const natural = (direction: QueryAST.OrderDirection = 'asc'): Order<any> =>
  new OrderClass({ kind: 'natural', direction });
/**
 * Order by a property of the current result shape.
 *
 * @performance O(1); builds an order AST node without sorting anything.
 */
export const property = <T>(property: keyof T & string, direction: QueryAST.OrderDirection): Order<T> =>
  new OrderClass({
    kind: 'property',
    property,
    direction,
  });

/**
 * Order by relevance rank (for FTS/vector search results).
 * Higher rank = better match. Default direction is 'desc' (best matches first).
 *
 * @performance O(1); builds an order AST node without sorting anything.
 */
export const rank = <T>(direction: QueryAST.OrderDirection = 'desc'): Order<T> =>
  new OrderClass({
    kind: 'rank',
    direction,
  });

/**
 * Order by the system `updatedAt` timestamp (last re-indexed). Default direction is 'desc'
 * (most-recently-updated first). Mirrors {@link Filter.updated}.
 *
 * @performance O(1); builds an order AST node without sorting anything.
 */
export const updated = <T>(direction: QueryAST.OrderDirection = 'desc'): Order<T> =>
  new OrderClass({
    kind: 'timestamp',
    field: 'updatedAt',
    direction,
  });

/**
 * Order by the system `createdAt` timestamp (first indexed). Default direction is 'desc'
 * (most-recently-created first). Mirrors {@link Filter.created}.
 *
 * @performance O(1); builds an order AST node without sorting anything.
 */
export const created = <T>(direction: QueryAST.OrderDirection = 'desc'): Order<T> =>
  new OrderClass({
    kind: 'timestamp',
    field: 'createdAt',
    direction,
  });
